/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PlayPlaylistContext } from 'types/playlists.types';
import { SelectedReleaseType } from 'app/components/Releases';
import { ReleaseType } from 'types/releases';
import { Referral, ReleaseAudioCount, Role, TierSelection } from './types';

export interface MixpanelState {
  selectedAudioCount?: number;
  referral?: string;
  referralOrigin?: string;
}

export const mixpanelInitialState: MixpanelState = {
  selectedAudioCount: 0,
  referralOrigin: undefined,
};

const slice = createSlice({
  name: 'mixpanel',
  initialState: mixpanelInitialState,
  reducers: {
    initiateV2VerificationAction(_, __: PayloadAction<string>) {},
    returnFromV2VerificationAction() {},
    connectToSoundcloudAttemptAction() {},
    connectedToSoundcloudAction() {},
    connectToTuneCoreAttemptAction(
      _,
      __: PayloadAction<{ page: 'Release' | 'Profile' }>
    ) {},
    connectedToTuneCoreAction() {},
    tuneCoreReleaseCreatedAction(_, __: PayloadAction<ReleaseAudioCount>) {},
    labelV2ReleaseCreatedAction(_, __: PayloadAction<ReleaseAudioCount>) {},
    tuneCoreReleaseExportCreatedAction(
      _,
      __: PayloadAction<ReleaseAudioCount>
    ) {},
    tuneCoreAudioSelectScreenOpenedAction() {},
    saveReasonForAccountDeleteAction(_, __: PayloadAction<string>) {},
    selectSongAction(_, __: PayloadAction<string>) {},
    clickNotificationAction(_, __: PayloadAction<string>) {},
    clickUploadButtonAction() {},
    createQrAction() {},
    audioSelectScreenOpenedAction() {},
    audioSelectScreenClosedAction(
      _,
      __: PayloadAction<{ releaseType: SelectedReleaseType | ReleaseType }>
    ) {},
    soundCloudReleaseCreatedAction(_, __: PayloadAction<ReleaseAudioCount>) {},
    soundcloudReleaseExportCreatedAction(
      _,
      __: PayloadAction<ReleaseAudioCount>
    ) {},
    openIntercomAction() {},
    setSelectedAudioCountAction(state, action: PayloadAction<number>) {
      state.selectedAudioCount = action.payload;
    },
    setReferralOriginAction(state, action: PayloadAction<string | undefined>) {
      state.referralOrigin = action.payload;
    },
    setReferralAction(state, action: PayloadAction<Referral>) {
      state.referral = action.payload.referral;
      state.referralOrigin = action.payload.referralOrigin;
    },
    clickCreateAccountAction() {},
    signUpStartedAction(_, __: PayloadAction<string>) {},
    signUpEmailPasswordAddedAction() {},
    signUpPersonalDetailsAddedAction() {},
    signUpMarketingOptInAction() {},
    signUpAccountCreatedAction(_, __: PayloadAction<Role>) {},
    signUpVerifiedAction() {},
    signUpResendVerificationAction() {},
    subscriptionSubscribeButtonAction() {},
    subscriptionManageButtonAction() {},
    subscriptionTierSelectedAction(_, __: PayloadAction<TierSelection>) {},
    subscriptionTierSelectionCancelledAction() {},
    subscriptionCancelledAction() {},
    subscriptionCreatedAction() {},
    searchStartedAction(_, __: PayloadAction<string>) {},
    searchClearedAction() {},
    searchSongSelectAction(_, __: PayloadAction<string>) {},
    searchCollaboratorSelectAction(_, __: PayloadAction<string>) {},
    searchCollaboratorSongSelectAction(_, __: PayloadAction<string>) {},
    playlistPlayLocation(
      _,
      __: PayloadAction<{ playlistGuid: string; location: PlayPlaylistContext }>
    ) {},
    playlistHeaderImageClickedAction(_, __: PayloadAction<string>) {},
    openProfileCardAction(_, __: PayloadAction<string>) {},
    addSongsToFolderAction(
      _,
      __: PayloadAction<{ folderId: string; songsAdded: number }>
    ) {},
    removeSongsFromFolderAction(
      _,
      __: PayloadAction<{ folderId: string; songsRemoved: number }>
    ) {},
    previewSharedAudioAction(
      _,
      __: PayloadAction<{
        songGuid: string;
        creditsShared: boolean;
        lyricsShared: boolean;
        downloadable: boolean;
      }>
    ) {},
    previewSharedPlaylistAction(
      _,
      __: PayloadAction<{
        playlistGuid: string;
        shareCount: number;
        creditsShared: boolean;
        lyricsShared: boolean;
        downloadable: boolean;
      }>
    ) {},
    openSharedCreditsAction(
      _,
      __: PayloadAction<{
        playlistGuid?: string;
        songGuid?: string;
      }>
    ) {},
    openSharedContactCardAction(
      _,
      __: PayloadAction<{
        playlistGuid?: string;
        songGuid?: string;
      }>
    ) {},
    openSharedLyricsAction(
      _,
      __: PayloadAction<{
        playlistGuid?: string;
        songGuid?: string;
      }>
    ) {},
    audioShareLyricsBlockedAction() {},
    audioShareCreditsBlockedAction() {},
    copySuggestionAction() {},
  },
});

export const {
  setSelectedAudioCountAction,
  audioSelectScreenOpenedAction,
  audioSelectScreenClosedAction,
  soundCloudReleaseCreatedAction,
  soundcloudReleaseExportCreatedAction,
  initiateV2VerificationAction,
  returnFromV2VerificationAction,
  connectToSoundcloudAttemptAction,
  connectedToSoundcloudAction,
  connectToTuneCoreAttemptAction,
  connectedToTuneCoreAction,
  tuneCoreReleaseCreatedAction,
  labelV2ReleaseCreatedAction,
  tuneCoreReleaseExportCreatedAction,
  tuneCoreAudioSelectScreenOpenedAction,
  clickNotificationAction,
  saveReasonForAccountDeleteAction,
  clickUploadButtonAction,
  createQrAction,
  openIntercomAction,
  setReferralOriginAction,
  setReferralAction,
  clickCreateAccountAction,
  signUpStartedAction,
  signUpEmailPasswordAddedAction,
  signUpPersonalDetailsAddedAction,
  signUpMarketingOptInAction,
  signUpAccountCreatedAction,
  signUpVerifiedAction,
  signUpResendVerificationAction,
  selectSongAction,
  subscriptionSubscribeButtonAction,
  subscriptionManageButtonAction,
  subscriptionTierSelectedAction,
  subscriptionTierSelectionCancelledAction,
  subscriptionCancelledAction,
  subscriptionCreatedAction,
  searchStartedAction,
  searchClearedAction,
  searchSongSelectAction,
  searchCollaboratorSelectAction,
  searchCollaboratorSongSelectAction,
  playlistPlayLocation,
  playlistHeaderImageClickedAction,
  openProfileCardAction,
  addSongsToFolderAction,
  removeSongsFromFolderAction,
  previewSharedAudioAction,
  previewSharedPlaylistAction,
  openSharedCreditsAction,
  openSharedContactCardAction,
  openSharedLyricsAction,
  audioShareLyricsBlockedAction,
  audioShareCreditsBlockedAction,
  copySuggestionAction,
} = slice.actions;

export const mixpanelReducer = slice.reducer;
